import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import food from "../assets/images/order/food.png";
import GroupOrderedItem from "../components/GroupOrderedItem";
import link from "../assets/images/group/link.png";
import edit from "../assets/images/group/edit2.png";
import clock from "../assets/images/group/clock.png";
import creditcard from "../assets/images/support/credit-card.png";
import locationImg from "../assets/images/location.png";
import rightarrow from "../assets/images/arrow-right.png";
import { orderService } from "../services";
import socketClient from "../utils/socketClient";

const GroupOrder = () => {
  const navigate = useNavigate();
  const { sessionId: routeSessionId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = routeSessionId || searchParams.get("sessionId") || "";

  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState<any>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);

  const fetchSessionDetails = useCallback(
    async (sid: string, showLoading = true) => {
      try {
        if (showLoading) setLoading(true);
        const response: any = await orderService.getGroupOrderSession(sid);
        if (response.status && response.data) {
          setSessionData(response.data);
          const expiresAt = new Date(response.data.session.expiresAt).getTime();
          const remainingSeconds = Math.max(
            0,
            Math.floor((expiresAt - Date.now()) / 1000)
          );
          setTimeRemaining(remainingSeconds);
        } else {
          toast.error("Failed to load group order details");
          navigate(-1);
        }
      } catch (error) {
        console.error("Error fetching session details:", error);
        toast.error("Failed to load group order details");
        navigate(-1);
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (!sessionId) {
      toast.error("Invalid group order link");
      navigate(-1);
      return;
    }
    fetchSessionDetails(sessionId);
  }, [sessionId, fetchSessionDetails, navigate]);

  // Socket realtime — aligned with mobile GroupOrder.tsx
  useEffect(() => {
    if (!sessionData?.session?._id) return;
    const sid = sessionData.session._id;

    socketClient.connect();
    socketClient.joinGroupSession(sid);

    const refresh = () => fetchSessionDetails(sid, false);

    socketClient.onMemberJoined(refresh);
    socketClient.onMemberLeft(refresh);
    socketClient.onHostChanged(() => {
      toast("The group host has been changed");
      refresh();
    });
    socketClient.onItemAdded(refresh);
    socketClient.onItemUpdated(refresh);
    socketClient.onItemRemoved(refresh);
    socketClient.onCheckoutStarted(() => {
      toast("The host has started the checkout process");
    });
    socketClient.onCheckoutFailed((data: { reason?: string }) => {
      toast.error(data?.reason || "Checkout failed");
    });
    socketClient.onSessionConfirmed((data: { orderId?: string }) => {
      toast.success("Group order confirmed");
      if (data?.orderId) navigate("/orders");
    });
    socketClient.onSessionExpired(() => {
      toast.error("This group order session has expired");
      navigate("/");
    });

    return () => {
      socketClient.leaveGroupSession(sid);
      socketClient.removeAllListeners();
    };
  }, [sessionData?.session?._id, fetchSessionDetails, navigate]);

  useEffect(() => {
    if (timeRemaining <= 0) return;
    const timer = setInterval(() => {
      setTimeRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [timeRemaining]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const inviteLink = useMemo(() => {
    if (!sessionData?.session?.inviteToken) return "";
    return `${window.location.origin}/group-order-join/${sessionData.session.inviteToken}`;
  }, [sessionData]);

  const handleCopyLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Invite link copied");
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const handleUpdateQty = async (itemId: string, quantity: number) => {
    if (!sessionId) return;
    try {
      if (quantity <= 0) {
        await orderService.deleteGroupOrderItem(sessionId, itemId);
      } else {
        await orderService.updateGroupOrderItem(sessionId, itemId, { quantity });
      }
      await fetchSessionDetails(sessionId, false);
    } catch (error: any) {
      toast.error(error?.message || "Failed to update item");
    }
  };

  const handleOrderNow = () => {
    if (!sessionData) return;
    navigate(
      `/group-order-checkout/${sessionData.session._id}?restaurantId=${sessionData.session.restaurantId}`
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <p className="dark:text-white">Loading group order...</p>
      </div>
    );
  }

  if (!sessionData) return null;

  const { session, members = [], items = [] } = sessionData;
  const address = session.deliveryAddress;

  const displayItems = items.map((item: any) => ({
    id: item._id,
    image: item.dish?.images?.[0]?.url || food,
    itemName: item.dish?.dishName || "Item",
    itemPrice: item.dish?.basePrice || 0,
    discountedPrice: item.dish?.basePrice || 0,
    quantity: `${item.quantity} Qty`,
    qty: item.quantity,
    foodType: item.dish?.foodType,
    addedBy:
      [item.addedByUser?.firstName, item.addedByUser?.lastName]
        .filter(Boolean)
        .join(" ") || "Member",
  }));

  return (
    <div className="flex flex-col md:flex-row border-2 bg-white dark:bg-gray-900 dark:border-gray-700 transition-colors duration-300 mt-[7.5rem] md:mt-[72px] min-h-screen">
      <div className="w-full md:w-[35%] border-r dark:border-gray-700 py-6">
        <div className="px-4 md:px-8">
          <h6 className="text-[#6A6A6A] dark:text-gray-400 text-left font-urbanist text-[16px] font-medium leading-[20px]">
            Group Order
          </h6>

          <div className="mt-4 w-full flex flex-col justify-center items-center gap-[25px]">
            <div className="flex flex-col justify-center items-center w-full">
              <div
                className="flex justify-center items-center my-[12px] gap-[8px] cursor-pointer px-[12px] py-[10px] bg-[#F5F5F5] dark:bg-gray-800 rounded-[8px] w-fit mx-auto hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                onClick={handleCopyLink}
              >
                <img src={link} alt="Link" className="h-[24px] w-[24px]" />
                <span className="text-[#6A6A6A] dark:text-gray-400 text-[16px] font-medium">
                  Copy Invite Link
                </span>
              </div>

              <div className="flex flex-col justify-center items-center">
                <div className="flex gap-4 items-center mt-[4px]">
                  <h6 className="text-[#050505] dark:text-white font-urbanist text-[22px] font-semibold leading-[140%]">
                    {(session as any).name || "Group Order"}
                  </h6>
                  <img src={edit} alt="Edit" className="w-[20px] h-[20px]" />
                </div>
                <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-sm mt-1">
                  Expires in {formatTime(timeRemaining)} · {members.length} members
                </p>
              </div>
            </div>

            <div className="border dark:border-gray-700 rounded-[16px] p-[12px] w-full">
              <div className="flex gap-[12px] items-center pb-[12px] border-b-2 dark:border-gray-700">
                <div className="flex justify-center items-center h-[48px] w-[48px] rounded-[16px] border-2 dark:border-gray-600">
                  <img src={clock} alt="clock" className="h-[24px] w-[24px]" />
                </div>
                <div>
                  <h6 className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[14px] font-medium">
                    When to Deliver
                  </h6>
                  <p className="text-[#050505] dark:text-white font-urbanist text-[16px]">
                    Deliver Now
                  </p>
                </div>
              </div>
              <div className="flex gap-[12px] items-center py-[12px] border-b-2 dark:border-gray-700">
                <div className="flex justify-center items-center h-[48px] w-[48px] rounded-[16px] border-2 dark:border-gray-600">
                  <img src={locationImg} alt="location" className="h-[24px] w-[24px]" />
                </div>
                <div>
                  <h6 className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[14px] font-medium">
                    Where to Deliver
                  </h6>
                  <p className="text-[#050505] dark:text-white font-urbanist text-[16px]">
                    {address
                      ? `${address.addressLine1}, ${address.city}`
                      : "No address"}
                  </p>
                </div>
              </div>
              <div className="flex gap-[12px] items-center mt-[12px]">
                <div className="flex justify-center items-center h-[48px] w-[48px] rounded-[16px] border-2 dark:border-gray-600">
                  <img src={creditcard} alt="credit card" className="h-[24px] w-[24px]" />
                </div>
                <div>
                  <h6 className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[14px] font-medium">
                    Who is Paying
                  </h6>
                  <p className="text-[#050505] dark:text-white font-urbanist text-[16px]">
                    Host is paying for this order
                  </p>
                </div>
              </div>
            </div>

            <button
              className="w-full flex items-center justify-center py-4 px-6 bg-[#F5F5F5] dark:bg-gray-800 rounded-[150px] text-[#7402A7] dark:text-purple-400 font-urbanist text-[18px] font-semibold hover:bg-gray-200 transition-colors"
              onClick={() =>
                navigate(`/restaurants/${session.restaurantId}`, {
                  state: { groupSessionId: session._id },
                })
              }
            >
              Add Items
              <img src={rightarrow} alt="arrow" className="inline-block ml-2 h-[24px] w-[24px]" />
            </button>

            <button
              className="w-full flex items-center justify-center py-4 px-6 bg-[#7402A7] dark:bg-purple-600 rounded-[150px] text-white font-urbanist text-[18px] font-semibold hover:bg-[#5f0289] transition-colors disabled:opacity-50"
              onClick={handleOrderNow}
              disabled={items.length === 0}
            >
              Order Now
              <img src={rightarrow} alt="arrow" className="inline-block ml-2 h-[24px] w-[24px]" />
            </button>
          </div>
        </div>
      </div>

      <div className="w-full md:w-[65%] p-4 md:p-8">
        <h6 className="text-[#050505] dark:text-white font-urbanist text-[20px] font-semibold mb-4">
          Group Items ({items.length})
        </h6>
        {displayItems.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">
            No items yet. Invite friends or add dishes.
          </p>
        ) : (
          <div className="space-y-3">
            {displayItems.map((item: any) => (
              <div key={item.id} className="relative">
                <GroupOrderedItem {...item} />
                <div className="absolute right-4 top-4 flex gap-2">
                  <button
                    className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded"
                    onClick={() => handleUpdateQty(item.id, item.qty - 1)}
                  >
                    −
                  </button>
                  <button
                    className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded"
                    onClick={() => handleUpdateQty(item.id, item.qty + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6">
          <h6 className="text-[#050505] dark:text-white font-urbanist text-[18px] font-semibold mb-3">
            Members
          </h6>
          <div className="flex flex-wrap gap-3">
            {members.map((member: any) => (
              <div
                key={member._id}
                className="px-3 py-2 rounded-full bg-[#F5F5F5] dark:bg-gray-800 text-sm dark:text-white"
              >
                {[member.user?.firstName, member.user?.lastName]
                  .filter(Boolean)
                  .join(" ") || "Member"}
                {member.role === "host" ? " (Host)" : ""}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupOrder;
